import { brand } from "../../../../../../config/theme";
import { useState, useMemo, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import { useIntegratorsStore } from "../../../../../../store/reseller-admin/integratorsStore";
import type { Integrator } from "../../../../../../store/reseller-admin/integratorsStore";
import { useIntegratorsList } from "../../../../../../features/reseller/queries";
import { useDebounce } from "../../../../../../hooks/useDebounce";
import type { AlertState } from "../types";

export interface CredentialFieldForm {
  field: string;
  label: string;
  secret: boolean;
}

export interface IntegratorFormData {
  name: string;
  active: boolean;
  adapter: string;
  auth_strategy: string;
  protocol: string;
  default_endpoint: string;
  credentials_schema: CredentialFieldForm[];
}

const defaultFormValues: IntegratorFormData = {
  name: "",
  active: true,
  adapter: "recurso_confiable",
  auth_strategy: "token-refresh",
  protocol: "rest",
  default_endpoint: "",
  credentials_schema: [],
};

interface FilterParams {
  page: number;
  limit: number;
  name?: string;
  active?: string;
}

const integratorToForm = (integrator: Integrator): IntegratorFormData => ({
  name: integrator.name || "",
  active: integrator.active ?? true,
  adapter: integrator.adapter || "",
  auth_strategy: integrator.auth_strategy || "",
  protocol: integrator.protocol || "",
  default_endpoint: integrator.default_endpoint || "",
  credentials_schema: (integrator.credentials_schema || []).map((c) => ({
    field: c.field || "",
    label: c.label || "",
    secret: c.secret === true,
  })),
});

export function useIntegratorForm() {
  const { t } = useTranslation("resellerAdmin");

  const [selectedIntegrator, setSelectedIntegrator] =
    useState<Integrator | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState<"details" | "edit" | "add">(
    "details"
  );
  const [alert, setAlert] = useState<AlertState>({
    show: false,
    title: "",
    message: "",
    type: "info",
  });

  const [showFormModal, setShowFormModal] = useState(false);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [editIntegratorId, setEditIntegratorId] = useState<string | null>(null);
  // Id del integrador cuyo detalle se está cargando para editar (loader en la fila/tarjeta).
  const [editingId, setEditingId] = useState<string | null>(null);
  const [originalData, setOriginalData] = useState<IntegratorFormData | null>(
    null
  );

  // Filter states
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedView, setSelectedView] = useState<"grid" | "list">("grid");
  const itemsPerPage = 8;
  const debouncedSearch = useDebounce(searchQuery);

  const {
    fetchIntegrator,
    createIntegrator,
    updateIntegrator,
    deleteIntegrator,
    fetchIntegratorsCount,
  } = useIntegratorsStore();

  const form = useForm<IntegratorFormData>({
    defaultValues: defaultFormValues,
  });

  const filterParams = useMemo(
    (): FilterParams => ({
      page: currentPage,
      limit: itemsPerPage,
      name: debouncedSearch || undefined,
      active: selectedStatus !== "all" ? selectedStatus : undefined,
    }),
    [currentPage, itemsPerPage, selectedStatus, debouncedSearch],
  );

  const integratorsQuery = useIntegratorsList(filterParams);
  const integrators = integratorsQuery.data?.data ?? [];
  const meta = integratorsQuery.data?.meta;
  const loading = integratorsQuery.isLoading;

  const queryClient = useQueryClient();
  const invalidateIntegrators = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["resellerIntegrators"] });
    queryClient.invalidateQueries({ queryKey: ["resellerCounts"] });
    fetchIntegratorsCount();
  }, [queryClient, fetchIntegratorsCount]);

  const handleStatusChange = (value: string) => {
    setSelectedStatus(value);
    setCurrentPage(1);
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setCurrentPage(1);
  };

  const handleDetailsClick = (integrator: Integrator) => {
    setSelectedIntegrator(integrator);
    setModalType("details");
    setIsModalOpen(true);
    document.body.style.overflow = "hidden";
  };

  const handleAddClick = () => {
    setIsEdit(false);
    setEditIntegratorId(null);
    setOriginalData(null);
    form.reset(defaultFormValues);
    setModalType("add");
    setShowFormModal(true);
    document.body.style.overflow = "hidden";
  };

  const handleEditClick = async (integrator: Integrator) => {
    setEditingId(integrator.id);
    try {
      const full = await fetchIntegrator(integrator.id);
      const integratorData =
        (full as { data?: Integrator })?.data ?? full ?? integrator;
      const resetData = integratorToForm(integratorData);

      form.reset(resetData);
      setOriginalData(resetData);
      setEditIntegratorId(integrator.id);
      setIsEdit(true);
      setModalType("edit");
      setShowFormModal(true);
      document.body.style.overflow = "hidden";
    } catch (error) {
      console.error("Error al cargar integrador:", error);
      await Swal.fire({
        title: t("integrators.alerts.error"),
        text: t("integrators.alerts.loadError"),
        icon: "error",
        confirmButtonText: t("integrators.actions.accept"),
        confirmButtonColor: "#ef4444",
      });
    } finally {
      setEditingId(null);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedIntegrator(null);
    document.body.style.overflow = "";
  };

  const closeFormModal = () => {
    setShowFormModal(false);
    setIsEdit(false);
    setEditIntegratorId(null);
    setOriginalData(null);
    setLoadingSubmit(false);
    form.reset(defaultFormValues);
    document.body.style.overflow = "";
  };

  const closeAlert = () => {
    setAlert((prev) => ({ ...prev, show: false }));
  };

  const hasFormChanges = () => {
    if (!isEdit || !originalData) return false;
    const currentValues = form.getValues();
    return JSON.stringify(currentValues) !== JSON.stringify(originalData);
  };

  const handleDeleteIntegrator = async (integrator: Integrator) => {
    const result = await Swal.fire({
      title: t("integrators.alerts.confirmDeleteTitle"),
      text: t("integrators.alerts.confirmDeleteText", { name: integrator.name }),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#6b7280",
      confirmButtonText: t("integrators.alerts.confirmDeleteBtn"),
      cancelButtonText: t("integrators.alerts.cancelBtn"),
    });

    if (!result.isConfirmed) return;

    try {
      await deleteIntegrator(integrator.id);
      await Swal.fire({
        title: t("integrators.alerts.success"),
        text: t("integrators.alerts.deleteSuccess"),
        icon: "success",
        confirmButtonText: t("integrators.actions.accept"),
        confirmButtonColor: brand.primary,
      });
      invalidateIntegrators();
      closeModal();
      closeFormModal();
    } catch (error) {
      console.error("Error al eliminar integrador:", error);
      await Swal.fire({
        title: t("integrators.alerts.error"),
        text: t("integrators.alerts.deleteError"),
        icon: "error",
        confirmButtonText: t("integrators.actions.accept"),
        confirmButtonColor: "#ef4444",
      });
    }
  };

  const handleSubmitIntegrator = async (data: IntegratorFormData) => {
    setLoadingSubmit(true);
    try {
      const payload = {
        name: data.name,
        active: data.active,
        adapter: data.adapter || null,
        auth_strategy: data.auth_strategy || null,
        protocol: data.protocol || null,
        default_endpoint: data.default_endpoint || null,
        credentials_schema: (data.credentials_schema || []).filter(
          (c) => c.field.trim() !== "" || c.label.trim() !== ""
        ),
      };

      if (isEdit && editIntegratorId) {
        await updateIntegrator(editIntegratorId, payload);
        await Swal.fire({
          title: t("integrators.alerts.success"),
          text: t("integrators.alerts.updateSuccess"),
          icon: "success",
          confirmButtonText: t("integrators.actions.accept"),
          confirmButtonColor: brand.primary,
        });
      } else {
        await createIntegrator(payload);
        await Swal.fire({
          title: t("integrators.alerts.success"),
          text: t("integrators.alerts.createSuccess"),
          icon: "success",
          confirmButtonText: t("integrators.actions.accept"),
          confirmButtonColor: brand.primary,
        });
      }

      closeFormModal();
      invalidateIntegrators();
    } catch (error: unknown) {
      const err = error as {
        response?: { data?: { message?: string } };
        message?: string;
      };
      console.error("Error al guardar integrador:", error);
      await Swal.fire({
        title: t("integrators.alerts.error"),
        text:
          err?.response?.data?.message ||
          err?.message ||
          (isEdit
            ? t("integrators.alerts.updateError")
            : t("integrators.alerts.createError")),
        icon: "error",
        confirmButtonText: t("integrators.actions.accept"),
        confirmButtonColor: "#ef4444",
      });
    } finally {
      setLoadingSubmit(false);
    }
  };

  return {
    // Data
    integrators,
    meta,
    loading,
    selectedIntegrator,
    alert,

    // Modal states
    isModalOpen,
    modalType,
    showFormModal,
    loadingSubmit,
    isEdit,
    editIntegratorId,
    editingId,

    // Form
    form,

    // Filter states
    selectedStatus,
    searchQuery,
    currentPage,
    selectedView,
    itemsPerPage,

    // Handlers
    handleStatusChange,
    handleSearchChange,
    handleDetailsClick,
    handleAddClick,
    handleEditClick,
    handleSubmitIntegrator,
    handleDeleteIntegrator,
    setCurrentPage,
    setSelectedView,
    closeModal,
    closeFormModal,
    closeAlert,
    hasFormChanges,
  };
}
